import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { InformModal } from '../../common/SharedComponents';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import DateBox from 'devextreme-react/date-box';
import { NumericFormat } from 'react-number-format';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { formatDate, formatCurrency, handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { RepaymentExitByType, RepaymentExitByTypeOptions, RepaymentLevelType, RepaymentType } from '../common/LoanConstants';
import { CommonLoanSummary } from '../common/LoanComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { CommonLoanSummaryInvokingFeatureType } from '../common/constants/CommonLoanSummaryInvokingFeatureType';

const RepaymentModal = () => {
    const {
        isOpen,
        entityLevel = RepaymentLevelType.Property,
        entityId,
        repaymentId = 0,
        overrageRepaymentId = 0,
    } = useSelector((state) => state.loan.repaymentModal);
    const { dispatch } = store;

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        formColumn: { paddingBottom: '3px', fontSize: 'small' },
        formColumnMonths: { paddingBottom: '3px', paddingTop: '30px' },
        formRow: { marginBottom: '0' },
        highlightedFormRow: {
            marginBottom: '0',
            color: 'darkred',
            fontWeight: 'bold',
        },
        bfisearch: {
            width: '250px',
            position: 'absolute',
            right: '20px',
            paddingTop: '15px',
        },
        infoSection: {
            display: 'inline-block',
            width: '100%',
            marginTop: '5px',
            marginBottom: '10px',
        },
        dataSection: {
            display: 'inline-block',
            width: '100%',
            marginBottom: '10px',
            marginTop: '10px',
        },
        warning: {
            padding: '10px',
            color: 'darkred',
            width: '100%',
            textAlign: 'center',
        },
        repaymentsTable: {
            marginLeft: '10px',
            marginRight: '10px',
            marginBottom: '20px',
            backgroundColor: 'whitesmoke',
            border: '1px solid gray',
            borderRadius: '5px 5px 0px 0px',
        },
        repaymentsTableHeader: {
            backgroundColor: 'lightgray',
            borderRadius: '5px 5px 0px 0px',
            borderBottom: '1px solid gray',
            paddingLeft: '5px',
            paddingTop: '3px',
            paddingBottom: '3px',
            fontSize: 'small',
        },
    };

    const [paymentData, set_paymentData] = useState(null);
    const [id, set_id] = useState(0);
    const [overrageId, set_overrageId] = useState(0);
    const [isFullPayment, set_isFullPayment] = useState(1);
    const [payDate, set_payDate] = useState(new Date());
    const [amount, set_amount] = useState('');
    const [notes, set_notes] = useState('');
    const [exitByTypeId, set_exitByTypeId] = useState(0);
    const [refiLenderName, set_refiLenderName] = useState(null);
    const [salePrice, set_salePrice] = useState(null);
    const [remainingPrincipal, set_remainingPrincipal] = useState(null);
    const [totalCommittedAmount, set_totalCommittedAmount] = useState(null);
    const [loanPaidToDate, set_loanPaidToDate] = useState(null);

    const [paymentType, set_paymentType] = useState(null);

    const [informModalOpen, set_informModalOpen] = useState(false);
    const [informModalLoanNotFullyRepaidOpen, set_informModalLoanNotFullyRepaidOpen] = useState(false);
    const [informModalHasForcePlacedInsuranceOpen, set_informModalHasForcePlacedInsuranceOpen] = useState(false);
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const [promissoryNoteRequiredButMissing, set_promissoryNoteRequiredButMissing] = useState(false);

    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const saveStages = {
        validateAmount: 1,
        validatePortfolioMoveToRepaid: 2,
        validateForcePlacedInsurance: 3,
        save: 4,
    };

    const confirmInformModalLoanNotFullyRepaidOpen = () => {
        set_informModalLoanNotFullyRepaidOpen(false);
        validateSubmission(null, null, saveStages.validateForcePlacedInsurance);
    };

    const confirmInformModalForceClosedInsuranceOpen = () => {
        set_informModalHasForcePlacedInsuranceOpen(false);
        validateSubmission(null, null, saveStages.save);
    };

    const _handleClose = () => dispatch(loanActions.setRepaymentModal({ repaymentModal: { isOpen: false } }));

    const isAmountValid = () => {
        let sum = 0;
        for (let x = 0; x < paymentData.repayments.length; x++) {
            const pmt = paymentData.repayments[x];
            if (pmt.id === id) {
                sum += amount;
            } else {
                sum += paymentData.repayments[x].amount;
            }
        }

        return sum <= totalCommittedAmount;
    };

    const alertPortfolioLoanWillNotBeRepaid = (pd) => {
        var amountToPay = isFullPayment ? remainingPrincipal : amount;
        if (
            pd.isPortfolio &&
            entityLevel === RepaymentLevelType.Property &&
            pd.promissoryNote > pd.totalLoanCommittedAmount &&
            pd.totalLoanCommittedAmount <= loanPaidToDate + amountToPay
        ) {
            return true;
        }
        return false;
    };

    const alertForcePlacedInsurance = (pd) => {
        return pd?.hasAnyForcePlacedInsurance === true;
    };

    const validateSubmission = async (e, paymentData, step) => {
        if (e) {
            e.preventDefault();
        }
        step = step ?? saveStages.validateAmount;

        if (step === saveStages.validateAmount) {
            if (!isAmountValid()) {
                set_informModalOpen(true);
                return;
            }
            step = saveStages.validatePortfolioMoveToRepaid;
        }

        if (step == saveStages.validatePortfolioMoveToRepaid) {
            if (alertPortfolioLoanWillNotBeRepaid(paymentData)) {
                set_informModalLoanNotFullyRepaidOpen(true);
                return;
            }
            step = saveStages.validateForcePlacedInsurance;
        }

        if ((step = saveStages.validateForcePlacedInsurance)) {
            if (alertForcePlacedInsurance(paymentData)) {
                set_informModalHasForcePlacedInsuranceOpen(true);
                return;
            }
            step = saveStages.save;
        }

        if (step == saveStages.save) {
            await savePayment();
        }
    };

    const savePayment = async () => {
        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            let response = null;
            if (entityLevel === RepaymentLevelType.Property) {
                const model = {
                    id,
                    payDate: new Date(payDate).toLocaleDateString('en-us'),
                    amount: isFullPayment ? 0 : amount,
                    notes,
                    isFullPayment,
                    exitByTypeId: isFullPayment ? exitByTypeId : 0,
                    refiLenderName: exitByTypeId === RepaymentExitByType.Refinance ? refiLenderName : null,
                    salePrice: exitByTypeId === RepaymentExitByType.Sale ? salePrice : null,
                };
                response = await loanApi.setPropertyRepayment(entityId, model);
            } else {
                const model = {
                    id: overrageId,
                    payDate: new Date(payDate).toLocaleDateString('en-us'),
                    amount: isFullPayment ? 0 : amount,
                    notes,
                    isFullPayment,
                    exitByTypeId: isFullPayment ? exitByTypeId : 0,
                    refiLenderName: exitByTypeId === RepaymentExitByType.Refinance ? refiLenderName : null,
                    salePrice: exitByTypeId === RepaymentExitByType.Sale ? salePrice : null,
                };
                response = await loanApi.setLoanRepayment(entityId, model);
            }
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Loan updated successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    useEffect(() => {
        async function entityIdChanged() {
            if (entityId && entityId > 0) {
                await loadPaymentData(entityLevel, entityId, repaymentId, overrageRepaymentId);
            } else {
                resetState();
            }
            set_loadingOpen(false);
        }

        entityIdChanged();
    }, [isOpen, entityLevel, entityId, repaymentId]);

    useEffect(() => {
        if (!paymentData) {
            return;
        }
    }, [paymentData]);

    const resetState = () => {
        set_id(0);
        set_overrageId(0);
        set_isFullPayment(1);
        set_paymentData(null);
        set_payDate(new Date());
        set_amount('');
        set_notes(null);
        set_exitByTypeId(0);
        set_refiLenderName(null);
        set_salePrice(null);
        set_remainingPrincipal(null);
        set_totalCommittedAmount(null);
        set_loanPaidToDate(null);
    };

    // const repayedToDateForIndex = (payments, lastIndex) => {
    //     let sum = 0;
    //     for (let x = 0; x < lastIndex; x++) {
    //         sum += payments[x].amount;
    //     }
    //     return sum;
    // };

    const loadPaymentData = async (lvl, id, repaymentId, overrageRepaymentId) => {
        set_loadingMessage('Loading...');
        set_loadingOpen(true);

        let response = null;
        if (lvl === RepaymentLevelType.Property) {
            response = await loanApi.getPropertyRepayment(id);
        } else {
            response = await loanApi.getLoanRepayment(id);
        }

        if (response.statusCode !== 200) {
            set_loadingOpen(false);
            return;
        }
        const loan = response.result;

        set_paymentData(loan);

        let activeCommittedAmount = null;
        if (loan?.isPortfolioProperty) {
            const propertyCommitment = loan.commitment + loan.originationFee;
            activeCommittedAmount = propertyCommitment;
        } else {
            activeCommittedAmount = loan?.promissoryNote;
        }
        set_totalCommittedAmount(activeCommittedAmount);

        if (overrageRepaymentId && overrageRepaymentId > 0) {
            const idx = loan.portfolioOverageRepayments.findIndex((a) => a.id === overrageRepaymentId);
            if (idx > -1) {
                const pmt = loan.portfolioOverageRepayments[idx];
                const paidToDate = loan.paidToDate;
                set_overrageId(overrageRepaymentId);
                set_isFullPayment(pmt.isFullPayment ? 1 : 0);
                set_payDate(pmt.payDate ? new Date(pmt.payDate) : null);
                set_amount(pmt.amount);
                set_salePrice(pmt.salePrice);
                set_exitByTypeId(parseInt(pmt.exitByTypeId));
                set_refiLenderName(pmt.refiLenderName);
                set_notes(pmt.notes);
                set_remainingPrincipal(activeCommittedAmount - (paidToDate - pmt.amount));
                set_loanPaidToDate(loan.loanPaidToDate - pmt.amount);
            }
        } else if (repaymentId && repaymentId > 0) {
            const idx = loan.repayments.findIndex((a) => a.id === repaymentId);
            if (idx > -1) {
                const pmt = loan.repayments[idx];
                //const paidToDate = repayedToDateForIndex(loan.repayments, idx);
                const paidToDate = loan.paidToDate;
                set_id(repaymentId);
                set_isFullPayment(pmt.isFullPayment ? 1 : 0);
                set_payDate(pmt.payDate ? new Date(pmt.payDate) : null);
                set_amount(pmt.amount);
                set_salePrice(pmt.salePrice);
                set_exitByTypeId(parseInt(pmt.exitByTypeId));
                set_refiLenderName(pmt.refiLenderName);
                set_notes(pmt.notes);
                set_remainingPrincipal(activeCommittedAmount - (paidToDate - pmt.amount));
                set_loanPaidToDate(loan.loanPaidToDate - pmt.amount);
            }
        } else {
            set_remainingPrincipal(activeCommittedAmount - loan.paidToDate);
            set_loanPaidToDate(loan.loanPaidToDate);
        }

        //Disable payment if no promissory note at the loan level
        if (!loan?.isPortfolioProperty && !loan?.promissoryNote) {
            set_promissoryNoteRequiredButMissing(true);
        } else {
            set_promissoryNoteRequiredButMissing(false);
        }

        set_loadingOpen(false);
    };

    const partialPayLoanDisabled = (pd) => {
        if (pd.isPortfolio && pd.commitment > 0 && pd.paidToDate < pd.commitment) {
            return true;
        }

        return false;
    };

    const getNewPrincipalAmount = () => {
        var newPrincipalAmount = null;
        if (amount && amount > 0 && remainingPrincipal) {
            newPrincipalAmount = remainingPrincipal - amount;
        }
        return newPrincipalAmount;
    };

    if (!isOpen || !paymentData) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '650px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1 style={{ whiteSpace: 'nowrap' }}>
                                        {entityLevel === RepaymentLevelType.Property ? 'Property' : 'Loan'} Repayment
                                    </h1>
                                    <div style={styles.bfisearch}>
                                        {/* <BfiNumberSearch loanTypeId={loan?.loanTypeId} onSelect={selectBfiNumber} /> */}
                                    </div>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                            </div>

                            <div
                                className='standard-form no-search'
                                style={{ padding: '0', margin: '0' }}
                            >
                                <form
                                    name='loanRepaymentForm'
                                    onSubmit={(event) => validateSubmission(event, paymentData, saveStages.validateAmount)}
                                >
                                    <div style={{ padding: '0px 7px' }}>
                                        <div
                                            style={styles.form}
                                            className={'standard-form'}
                                        >
                                            <div style={{ height: 'auto', backgroundColor: 'whitesmoke', marginTop: '5px' }}>
                                                {promissoryNoteRequiredButMissing && (
                                                    <div style={styles.warning}>
                                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                                        &nbsp;Loan does not have a Promissory Note Amount.
                                                    </div>
                                                )}
                                                {paymentData.promissoryNote != null &&
                                                    paymentData.totalLoanCommittedAmount > paymentData.promissoryNote && (
                                                        <div style={styles.warning}>
                                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                                            &nbsp;Promissory Note Amount is less than the Approved Loan Amount.
                                                        </div>
                                                    )}
                                                {entityLevel === RepaymentLevelType.Loan &&
                                                    partialPayLoanDisabled(paymentData) && (
                                                        <div style={styles.warning}>
                                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                                            &nbsp;Partial payments cannot be made for portfolio loans unless all
                                                            properties are repaid.
                                                        </div>
                                                    )}
                                            </div>
                                            <div style={styles.dataSection}>
                                                <h6>{id != 0 || overrageId != 0 ? 'Edit' : 'New'} Payment</h6>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-12'
                                                        style={styles.formColumn}
                                                    >
                                                        <input
                                                            type='radio'
                                                            name='paymenttype'
                                                            value={isFullPayment}
                                                            checked={isFullPayment === 1}
                                                            onChange={(e) => set_isFullPayment(1)}
                                                            disabled={repaymentId}
                                                        />
                                                        &nbsp;Whole &nbsp;&nbsp;&nbsp;
                                                        <input
                                                            type='radio'
                                                            name='paymenttype'
                                                            value={!isFullPayment}
                                                            checked={isFullPayment === 0}
                                                            onChange={(e) => set_isFullPayment(0)}
                                                            disabled={
                                                                (entityLevel === RepaymentLevelType.Loan &&
                                                                    partialPayLoanDisabled(paymentData)) ||
                                                                repaymentId
                                                            }
                                                        />
                                                        &nbsp;Partial
                                                    </div>
                                                </div>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-4'
                                                        style={styles.formColumn}
                                                    >
                                                        <label htmlFor='payDate'>
                                                            Pay Date <span className='req'>*</span>
                                                        </label>
                                                        <DateBox
                                                            id='payDate'
                                                            value={payDate}
                                                            onValueChange={set_payDate}
                                                            required={true}
                                                        />
                                                    </div>

                                                    {isFullPayment === 1 && (
                                                        <div
                                                            className='columns small-12 large-4'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='principal'>Remaining Principal: </label>
                                                            <span className='helper'>
                                                                <span className='tooltip-trigger'>
                                                                    <FontAwesomeIcon icon={faQuestionCircle} />
                                                                </span>
                                                                <span className='text'>
                                                                    Remaining Principal
                                                                    <br />
                                                                    <br />
                                                                    This amount does not include any interest.
                                                                </span>
                                                            </span>
                                                            <NumericFormat
                                                                style={{
                                                                    border: 'none',
                                                                    backgroundColor: 'initial',
                                                                }}
                                                                thousandSeparator=','
                                                                prefix='$'
                                                                id='principal'
                                                                name='principal'
                                                                placeholder='$'
                                                                value={remainingPrincipal}
                                                                decimalScale={2}
                                                                allowNegative={true}
                                                                displayType='input'
                                                                disabled
                                                                fixedDecimalScale
                                                            />
                                                            <span className='helper'>
                                                                <span className='tooltip-trigger'>
                                                                    <FontAwesomeIcon icon={faQuestionCircle} />
                                                                </span>
                                                                <span className='text'>
                                                                    Remaining Principal
                                                                    <br />
                                                                    <br />
                                                                    This amount does not include any interest.
                                                                </span>
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                {isFullPayment === 1 && (
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-3'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='exitByTypeId'>
                                                                Exit By <span className='req'>*</span>
                                                            </label>
                                                            <select
                                                                id='exitByTypeId'
                                                                value={exitByTypeId}
                                                                onChange={(event) =>
                                                                    set_exitByTypeId(parseInt(event.target.value))
                                                                }
                                                                required={true}
                                                            >
                                                                {RepaymentExitByTypeOptions.map((fs) => {
                                                                    return (
                                                                        <option
                                                                            key={fs.id}
                                                                            value={fs.id}
                                                                        >
                                                                            {fs.name}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                        </div>
                                                        {exitByTypeId === RepaymentExitByType.Refinance && (
                                                            <div
                                                                className='columns small-12 large-9'
                                                                style={styles.formColumn}
                                                            >
                                                                <label htmlFor='refiLenderName'>
                                                                    Lender Name <span className='req'>*</span>
                                                                </label>
                                                                <input
                                                                    type='text'
                                                                    id='refiLenderName'
                                                                    value={refiLenderName}
                                                                    onChange={(e) => set_refiLenderName(e.target.value)}
                                                                    maxLength='100'
                                                                    required={true}
                                                                />
                                                            </div>
                                                        )}
                                                        {exitByTypeId === RepaymentExitByType.Sale && (
                                                            <div
                                                                className='columns small-12 large-5'
                                                                style={styles.formColumn}
                                                            >
                                                                <label htmlFor='salePrice'>
                                                                    Gross Sale Price <span className='req'>*</span>
                                                                </label>
                                                                <NumericFormat
                                                                    thousandSeparator=','
                                                                    prefix='$'
                                                                    id='salePrice'
                                                                    name='salePrice'
                                                                    placeholder='$'
                                                                    value={salePrice}
                                                                    decimalScale={2}
                                                                    allowNegative={false}
                                                                    onValueChange={(values) => {
                                                                        set_salePrice(values.floatValue);
                                                                    }}
                                                                    required={true}
                                                                    isAllowed={(values, sourceInfo) => {
                                                                        const value = values?.floatValue;
                                                                        return (
                                                                            value === undefined || value === null || value >= 0
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {isFullPayment === 0 && (
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-9'
                                                            style={styles.formColumn}
                                                        >
                                                            <table>
                                                                <tr>
                                                                    <td
                                                                        style={{
                                                                            width: '220px',
                                                                        }}
                                                                    >
                                                                        <label htmlFor='principal'>Remaining Principal: </label>
                                                                    </td>
                                                                    <td>
                                                                        <NumericFormat
                                                                            style={{
                                                                                border: 'none',
                                                                                backgroundColor: 'initial',
                                                                            }}
                                                                            thousandSeparator=','
                                                                            prefix='$'
                                                                            id='principal'
                                                                            name='principal'
                                                                            placeholder='$'
                                                                            value={remainingPrincipal}
                                                                            decimalScale={2}
                                                                            allowNegative={true}
                                                                            displayType='input'
                                                                            disabled
                                                                        />
                                                                        <span className='helper'>
                                                                            <span className='tooltip-trigger'>
                                                                                <FontAwesomeIcon icon={faQuestionCircle} />
                                                                            </span>
                                                                            <span className='text'>
                                                                                Remaining Principal
                                                                                <br />
                                                                                <br />
                                                                                This amount does not include any interest.
                                                                            </span>
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td>
                                                                        <label htmlFor='amount'>
                                                                            Amount Paid: <span className='req'>*</span>
                                                                        </label>
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            paddingBottom: '3px',
                                                                        }}
                                                                    >
                                                                        <NumericFormat
                                                                            thousandSeparator=','
                                                                            prefix='$'
                                                                            id='amount'
                                                                            name='amount'
                                                                            placeholder='$'
                                                                            value={amount}
                                                                            decimalScale={2}
                                                                            allowNegative={false}
                                                                            onValueChange={(values) => {
                                                                                set_amount(values.floatValue);
                                                                            }}
                                                                            required={true}
                                                                            isAllowed={(values, sourceInfo) => {
                                                                                const value = values?.floatValue;
                                                                                return (
                                                                                    value === undefined ||
                                                                                    value === null ||
                                                                                    (value > 0 && value <= remainingPrincipal)
                                                                                );
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                                <tr
                                                                    style={{
                                                                        borderTop: '1px solid silver',
                                                                    }}
                                                                >
                                                                    <td>
                                                                        <label htmlFor='payDate'>New Principal: </label>
                                                                    </td>
                                                                    <td>
                                                                        <NumericFormat
                                                                            style={{
                                                                                border: 'none',
                                                                                backgroundColor: 'initial',
                                                                            }}
                                                                            thousandSeparator=','
                                                                            prefix='$'
                                                                            id='newPrincipal'
                                                                            name='newPrincipal'
                                                                            value={getNewPrincipalAmount()}
                                                                            decimalScale={2}
                                                                            displayType='input'
                                                                            disabled
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='notes'>Notes</label>
                                                        <textarea
                                                            value={notes}
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                            rows={2}
                                                            id='notes'
                                                            onChange={(e) => set_notes(e.target.value)}
                                                            required={false}
                                                            placeholder=''
                                                            maxLength={2000}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {paymentData.repayments && paymentData.repayments.length > 0 && (
                                                <div style={styles.infoSection}>
                                                    <h6>Payment History</h6>
                                                    <div
                                                    // className='row'
                                                    // style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={(styles.formColumn, { paddingBottom: '10px' })}
                                                        >
                                                            <b>Commitment:</b> {formatCurrency(totalCommittedAmount, 0)}
                                                        </div>
                                                    </div>
                                                    <div style={styles.repaymentsTable}>
                                                        <div style={styles.repaymentsTableHeader}>
                                                            {paymentData.isPortfolio ? 'Property Repayments' : 'Repayments'}
                                                        </div>
                                                        <div
                                                            className='row'
                                                            style={styles.formRow}
                                                        >
                                                            <div
                                                                className='columns small-12 large-1'
                                                                style={styles.formColumn}
                                                            >
                                                                <b>
                                                                    <u>#</u>
                                                                </b>
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-2'
                                                                style={styles.formColumn}
                                                            >
                                                                <b>
                                                                    <u>Date</u>
                                                                </b>
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-3'
                                                                style={styles.formColumn}
                                                            >
                                                                <b>
                                                                    <u>Amount</u>
                                                                </b>
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-3'
                                                                style={styles.formColumn}
                                                            >
                                                                <b>
                                                                    <u>Rem. Principal</u>
                                                                </b>
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-3'
                                                                style={styles.formColumn}
                                                            >
                                                                <b>
                                                                    <u>BFI #</u>
                                                                </b>
                                                            </div>
                                                        </div>
                                                        {paymentData.repayments.map((r, index) => {
                                                            return (
                                                                <div
                                                                    className='row'
                                                                    style={
                                                                        id === r.id ? styles.highlightedFormRow : styles.formRow
                                                                    }
                                                                >
                                                                    <div
                                                                        className='columns small-12 large-1'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        {index + 1}
                                                                    </div>
                                                                    <div
                                                                        className='columns small-12 large-2'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        {formatDate(r.payDate)}
                                                                    </div>
                                                                    <div
                                                                        className='columns small-12 large-3'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        {formatCurrency(r.amount, 2)}
                                                                    </div>
                                                                    <div
                                                                        className='columns small-12 large-3'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        {formatCurrency(r.remainingPrincipal, 2)}
                                                                    </div>
                                                                    <div
                                                                        className='columns small-12 large-3'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        {r.bfiNumber}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {paymentData.portfolioOverageRepayments &&
                                                        paymentData.portfolioOverageRepayments.length > 0 && (
                                                            <div style={styles.repaymentsTable}>
                                                                <div style={styles.repaymentsTableHeader}>
                                                                    Portfolio Overage Repayments
                                                                </div>
                                                                <div
                                                                    className='row'
                                                                    style={styles.formRow}
                                                                >
                                                                    <div
                                                                        className='columns small-12 large-1'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        <b>
                                                                            <u>#</u>
                                                                        </b>
                                                                    </div>
                                                                    <div
                                                                        className='columns small-12 large-2'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        <b>
                                                                            <u>Date</u>
                                                                        </b>
                                                                    </div>
                                                                    <div
                                                                        className='columns small-12 large-3'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        <b>
                                                                            <u>Amount</u>
                                                                        </b>
                                                                    </div>
                                                                    <div
                                                                        className='columns small-12 large-3'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        <b>
                                                                            <u>Rem. Principal</u>
                                                                        </b>
                                                                    </div>
                                                                    <div
                                                                        className='columns small-12 large-3'
                                                                        style={styles.formColumn}
                                                                    >
                                                                        <b>
                                                                            <u>BFI #</u>
                                                                        </b>
                                                                    </div>
                                                                </div>
                                                                {paymentData.portfolioOverageRepayments.map((or, index) => {
                                                                    return (
                                                                        <div
                                                                            className='row'
                                                                            style={
                                                                                overrageId === or.id
                                                                                    ? styles.highlightedFormRow
                                                                                    : styles.formRow
                                                                            }
                                                                        >
                                                                            <div
                                                                                className='columns small-12 large-1'
                                                                                style={styles.formColumn}
                                                                            >
                                                                                {index + 1}
                                                                            </div>
                                                                            <div
                                                                                className='columns small-12 large-2'
                                                                                style={styles.formColumn}
                                                                            >
                                                                                {formatDate(or.payDate)}
                                                                            </div>
                                                                            <div
                                                                                className='columns small-12 large-3'
                                                                                style={styles.formColumn}
                                                                            >
                                                                                {formatCurrency(or.amount, 2)}
                                                                            </div>
                                                                            <div
                                                                                className='columns small-12 large-3'
                                                                                style={styles.formColumn}
                                                                            >
                                                                                {formatCurrency(or.remainingPrincipal, 2)}
                                                                            </div>
                                                                            <div
                                                                                className='columns small-12 large-3'
                                                                                style={styles.formColumn}
                                                                            >
                                                                                {or.bfiNumber}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                </div>
                                            )}
                                            <CommonLoanSummary
                                                loan={paymentData}
                                                invokingFeature={CommonLoanSummaryInvokingFeatureType.LoanRepayment}
                                            />
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            disabled={promissoryNoteRequiredButMissing}
                                            type='submit'
                                            className='button primary'
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <InformModal
                isOpen={informModalOpen}
                title={'Error'}
                text={'The amount would exceed the total Principal!'}
                confirmCallback={() => set_informModalOpen(false)}
            />
            <InformModal
                isOpen={informModalLoanNotFullyRepaidOpen}
                title={'Loan Status'}
                text={
                    'All properties have been repaid, but there is still a balance at the loan level. Repay the balance at the loan level to move the loan to the repaid status.'
                }
                confirmCallback={() => confirmInformModalLoanNotFullyRepaidOpen()}
            />
            <InformModal
                isOpen={informModalHasForcePlacedInsuranceOpen}
                title={'Force Placed Insurance'}
                text={
                    'This property has force placed insurance.  Please reach out to the primary on insurance or POC so the policy can be cancelled.'
                }
                confirmCallback={() => confirmInformModalForceClosedInsuranceOpen()}
            />
        </>
    );
};

export default RepaymentModal;
